import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import Animated from 'react-native-reanimated'
import { Image as ExpoImage } from 'expo-image'
import { toPlaceholder, GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'
import {
  Calendar,
  MapPin,
  Shield,
  Car,
  MessageCircle,
  AlertTriangle,
  Clock3,
  CheckCircle,
} from '@/components/ui/icons'
import { useAuth } from '@/lib/hooks/use-auth'
import { messagingService } from '@/lib/services/messaging.service'
import { toast } from '@/lib/toast'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { Skeleton } from '@/components/ui/Skeleton'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenState } from '@/components/ui/ScreenState'
import { Pressable } from '@/components/ui/Pressable'
import { DisputeStatusPill } from '@/components/disputes/DisputeStatusPill'
import { CoverageAssistanceCard } from '@/components/insurance/CoverageAssistanceCard'
import { HostApprovalCard } from '@/components/booking/HostApprovalCard'
import { HostGatewayFeeCard } from '@/components/booking/HostGatewayFeeCard'
import { useBooking } from '@/lib/hooks/use-booking'
import { useInspection } from '@/lib/hooks/use-inspection'
import { useReviewEligibility } from '@/lib/hooks/use-reviews'
import { InspectionEvidenceBlock } from '@/components/trip/InspectionEvidenceBlock'
import { useBookingStore } from '@/lib/stores/booking.store'
import { useTripDisputes } from '@/lib/hooks/use-disputes'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance, type EntranceFn } from '@/lib/hooks/use-entrance'
import {
  bookingDateKey,
  bookingTimeHHmm,
  formatBookingDate,
  formatBookingDateTime,
} from '@/lib/utils/dates'
import type { InspectionDetail, InspectionAssuranceStatus } from '@/lib/types/inspection.types'
import { formatCurrency } from '@/lib/utils/format'

/** Height of the vehicle frame on this screen. Shared with the skeleton so
 *  the swap does not shift every block below it. */
const TRIP_HERO_HEIGHT = 200

function InfoRow({
  icon: Icon,
  label,
  value,
  colors,
  testID,
}: {
  icon: typeof Calendar
  label: string
  value: string
  colors: ReturnType<typeof useSemanticColors>
  testID?: string
}) {
  return (
    <View
      testID={testID}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        paddingVertical: SPACING.sm,
      }}
    >
      <Icon size={18} color={colors.textSecondary} strokeWidth={1.8} />
      <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary, flex: 1 }}>
        {label}
      </Text>
      <Text
        style={{
          maxWidth: '52%',
          flexShrink: 1,
          fontSize: 14,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
          textAlign: 'right',
        }}
        numberOfLines={2}
      >
        {value}
      </Text>
    </View>
  )
}

export default function TripDetailScreen() {
  const { tripId } = useLocalSearchParams<{ tripId: string }>()
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useScreenInsets()
  const { user } = useAuth()
  const bookingQuery = useBooking(tripId)
  const enter = useEntrance(!bookingQuery.isLoading)
  const { data: checkinInspection } = useInspection(tripId, 'checkin')
  const { data: checkoutInspection } = useInspection(tripId, 'checkout')
  // Una persona participante califica el viaje completo una sola vez. El
  // servidor deriva después las reputaciones correspondientes a su rol.
  const reviewTrip = bookingQuery.data
  const isTripParticipant =
    !!user?.id && (reviewTrip?.guestId === user.id || reviewTrip?.hostId === user.id)
  const tripReviewEligibility = useReviewEligibility(
    reviewTrip?.status === 'completed' && isTripParticipant ? tripId : undefined,
  )

  const formatDate = (iso: string) => {
    if (iso.includes('T')) {
      return formatBookingDateTime(iso, i18n.language, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    }
    return formatBookingDate(iso, i18n.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  // One container, one JSX position. The loading branch used to re-wrap its
  // own copy, so the whole screen was torn down and rebuilt on the
  // loading -> content transition. ScreenState owns what varies — and it
  // gives a failed booking fetch a retry instead of the skeleton that used
  // to hang there forever (`!booking` was indistinguishable from loading).
  return (
    <ScreenContainer scroll={false} card={false}>
      {/* The header is hoisted out of the three branches below. It used to be
          rendered inside the content ScrollView and inside the skeleton, but
          not by the failure state — so a booking that failed to load left the
          user with no back control and nothing but the tab bar to escape
          with, its title pinned to the very top edge of the screen. */}
      <ScreenHeader title={t('trips.view_details')} />
      <View
        style={{
          flex: 1,
          // The failure state is a shrink-wrapped block; centring it in the
          // remaining height stops it hanging off the header with 1400pt of
          // empty background underneath. Loading and content stay top-aligned.
          justifyContent: bookingQuery.isError ? 'center' : 'flex-start',
        }}
      >
        <ScreenState
          query={bookingQuery}
          skeleton={
            <View style={{ padding: SPACING.lg, gap: SPACING.md }}>
              <Skeleton width="100%" height={TRIP_HERO_HEIGHT} borderRadius={RADIUS.lg} />
              <Skeleton width="60%" height={22} borderRadius={RADIUS.sm} />
              <Skeleton width="100%" height={120} borderRadius={RADIUS.lg} />
            </View>
          }
        >
          {(booking) => {
            const statusColors: Record<string, string> = {
              confirmed: colors.success,
              active: colors.info,
              completed: colors.textMuted,
              cancelled: COLORS.coral.DEFAULT,
              pending_payment: colors.warning,
              awaiting_host: colors.warning,
              pending_pickup: colors.warning,
              pending_return: colors.info,
              disputed: COLORS.coral.DEFAULT,
            }
            const isHostViewer = user?.id === booking.hostId
            const isGuestViewer = user?.id === booking.guestId

            /**
             * Qué decirle a alguien sobre el depósito, y cuándo callarse.
             *
             * TRES COLUMNAS, NO DOS. `deposit_held_cents` es lo que salió de la
             * tarjeta, `deposit_released_cents` lo que ya volvió, y
             * `deposit_claimed_cents` lo que una disputa le adjudicó al
             * anfitrión y por lo tanto no vuelve. Lo pendiente es la resta de
             * las tres. Con solo dos, a quien puso $500 y recibe $300 se le
             * mostraba la resta sin el porqué.
             *
             * QUIÉN MIRA CAMBIA EL TEXTO, no solo el tono. Esta pantalla la
             * abren los dos lados, y la copia estaba escrita entera desde el
             * lado del que paga: al anfitrión se le decía "se te devuelve" y
             * "tu banco" sobre plata que no es suya. Tampoco se le esconde --el
             * depósito es la garantía que cubre daños en SU carro y necesita
             * verlo--; lo que cambia es la persona gramatical.
             *
             * UNA RENTA CANCELADA NO "CIERRA". Decirle "se te devuelve al
             * cerrar la renta" a alguien que ya canceló es prometerle un evento
             * que no va a ocurrir, así que cancelada y completada comparten el
             * estado "en devolución". Desde hoy `refund-payment` registra la
             * devolución al anular el cobro, así que el caso normal ni siquiera
             * llega acá: llega como devuelto.
             *
             * Y LA COPIA NO NOMBRA EL CARRIL. Decía "a la misma tarjeta" y el
             * sistema no sabe por dónde sale: hoy la devolución es un movimiento
             * manual en la pasarela. Se promete lo que sí es cierto --que se
             * está gestionando y que avisamos-- y nada más.
             *
             * Nada retenido, nada devuelto y nada adjudicado -> `null`, y no se
             * dibuja nada. Ese es el caso más común de todos: un viaje cuyo
             * anfitrión no pide depósito.
             */
            const viewerSuffix = isHostViewer ? '_host' : ''
            const pendienteCents = Math.max(
              0,
              booking.depositHeldCents - booking.depositReleasedCents - booking.depositClaimedCents,
            )
            const enDevolucion = booking.status === 'completed' || booking.status === 'cancelled'
            const depositState =
              pendienteCents > 0
                ? {
                    amountCents: pendienteCents,
                    labelKey: enDevolucion
                      ? `deposit_returning${viewerSuffix}`
                      : `deposit_held${viewerSuffix}`,
                    noteKey: enDevolucion
                      ? `deposit_returning${viewerSuffix}_note`
                      : `deposit_held${viewerSuffix}_note`,
                  }
                : booking.depositReleasedCents > 0
                  ? {
                      amountCents: booking.depositReleasedCents,
                      labelKey: `deposit_returned${viewerSuffix}`,
                      noteKey: `deposit_returned${viewerSuffix}_note`,
                    }
                  : null

            // La parte adjudicada se muestra APARTE y siempre que exista, aunque
            // el resto ya se haya devuelto: es la unica linea que explica por
            // que la resta no cuadra.
            const depositClaimed =
              booking.depositClaimedCents > 0
                ? {
                    amountCents: booking.depositClaimedCents,
                    labelKey: `deposit_claimed${viewerSuffix}`,
                    noteKey: `deposit_claimed${viewerSuffix}_note`,
                  }
                : null

            const counterpartyId = isHostViewer ? booking.guestId : booking.hostId
            const counterpartyName = isHostViewer
              ? booking.guestDisplayName
              : booking.hostDisplayName
            const counterpartyAvatarUrl = isHostViewer
              ? booking.guestAvatarUrl
              : booking.hostAvatarUrl
            const counterpartyLabel = isHostViewer
              ? t('vehicle_detail.guest')
              : t('vehicle_detail.host')
            const contactCounterpartyLabel = isHostViewer
              ? t('vehicle_detail.contact_guest')
              : t('vehicle_detail.contact_host')
            const insuranceLabel =
              booking.insuranceModel === 'fleet'
                ? t('vehicle_detail.host_policy_coverage')
                : booking.insuranceModel === 'platform'
                  ? t('vehicle_detail.platform_insurance_coming_soon')
                  : t(`listing.insurance_${booking.insuranceModel}` as const)

            return (
              <ScrollView
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={{ paddingBottom: insets.bottom + SPACING.xl }}
              >
                {/* Vehicle image */}
                <Animated.View collapsable={false} entering={enter(1)}>
                  <View
                    style={{
                      height: TRIP_HERO_HEIGHT,
                      marginHorizontal: SPACING.lg,
                      borderRadius: RADIUS.lg,
                      overflow: 'hidden',
                      backgroundColor: colors.surfaceSubtle,
                    }}
                  >
                    {booking.vehiclePrimaryImageUrl ? (
                      <ExpoImage
                        source={booking.vehiclePrimaryImageUrl}
                        placeholder={toPlaceholder(
                          booking.vehiclePrimaryImageBlurhash ?? GENERIC_IMAGE_BLURHASH,
                        )}
                        style={{ width: '100%', height: '100%' }}
                        contentFit="cover"
                      />
                    ) : (
                      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <Car size={40} color={colors.textMuted} strokeWidth={1.3} />
                      </View>
                    )}
                  </View>
                </Animated.View>

                <View style={{ paddingHorizontal: SPACING.lg }}>
                  {/* Title + status */}
                  <Animated.View
                    collapsable={false}
                    entering={enter(2)}
                    style={{ marginTop: SPACING.md }}
                  >
                    <Text
                      testID="trip-detail.vehicle-name"
                      style={{
                        fontSize: 22,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {booking.vehicleTitle}
                    </Text>
                    <View
                      testID="trip-detail.status"
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: SPACING.xs,
                        marginTop: SPACING.xs,
                      }}
                    >
                      <View
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: statusColors[booking.status] ?? colors.textMuted,
                        }}
                      />
                      <Text
                        style={{
                          fontSize: 14,
                          fontFamily: FONTS.bodyBold,
                          color: statusColors[booking.status] ?? colors.textSecondary,
                        }}
                      >
                        {t(`hosting.booking_status_${booking.status}`)}
                      </Text>
                    </View>
                    <Text
                      style={{
                        fontSize: 13,
                        fontFamily: FONTS.body,
                        color: colors.textMuted,
                        marginTop: SPACING.xs,
                      }}
                    >
                      {booking.bookingCode}
                    </Text>
                  </Animated.View>

                  {booking.status === 'awaiting_host' && (
                    <HostApprovalCard
                      rentalId={booking.id}
                      deadlineAt={booking.hostResponseDeadlineAt}
                      rentalTermsVersion={booking.rentalTermsVersion}
                      isHost={isHostViewer}
                      onResolved={() => bookingQuery.refetch()}
                    />
                  )}
                  {isHostViewer &&
                    booking.status === 'cancelled' &&
                    booking.rentalTermsVersion === '2.0' && (
                      <HostGatewayFeeCard rentalId={booking.id} />
                    )}

                  {/* Details card */}
                  <Animated.View
                    collapsable={false}
                    entering={enter(3)}
                    style={{
                      marginTop: SPACING.lg,
                      backgroundColor: colors.surface,
                      borderRadius: RADIUS.lg,
                      padding: SPACING.md,
                      ...WEB_SHADOWS.sm,
                    }}
                  >
                    <InfoRow
                      icon={Calendar}
                      label={t('confirmation.dates')}
                      value={`${formatDate(booking.startAt)} → ${formatDate(booking.endAt)}`}
                      colors={colors}
                      testID="trip-detail.date-range"
                    />
                    <InfoRow
                      icon={MapPin}
                      label={t('booking.pickup_location')}
                      value={booking.pickupAddress}
                      colors={colors}
                    />
                    <InfoRow
                      icon={Shield}
                      label={t('booking.insurance')}
                      value={insuranceLabel}
                      colors={colors}
                    />
                  </Animated.View>

                  {booking.insuranceModel === 'platform' && (
                    <Animated.View
                      collapsable={false}
                      entering={enter(4)}
                      style={{ marginTop: SPACING.md }}
                    >
                      <CoverageAssistanceCard compact testID="trip-detail.assistance-card" />
                    </Animated.View>
                  )}

                  {/* Price breakdown */}
                  <Animated.View
                    testID="trip-detail.total-price"
                    collapsable={false}
                    entering={enter(4)}
                    style={{
                      marginTop: SPACING.md,
                      backgroundColor: colors.surface,
                      borderRadius: RADIUS.lg,
                      padding: SPACING.md,
                      ...WEB_SHADOWS.sm,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 16,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                        marginBottom: SPACING.sm,
                      }}
                    >
                      {t('booking.price_breakdown')}
                    </Text>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <Text
                        style={{
                          fontSize: 16,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                      >
                        {t('booking.total')}
                      </Text>
                      <Text
                        style={{
                          fontSize: 16,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textAccent,
                        }}
                      >
                        {formatCurrency(booking.totalCents)}
                      </Text>
                    </View>

                    {/*
                      El depósito, que hasta hoy esta pantalla no mencionaba.
                      A alguien le podían salir $500 de la tarjeta y no tenía
                      un solo lugar donde ver que estaban ahí ni cuándo vuelven.

                      Se lee del estado REAL --lo retenido y lo ya devuelto-- y
                      no del monto que el host pide, porque un viaje sin pagar
                      tiene lo segundo y no lo primero. Si no hay nada retenido
                      la fila no existe: una línea que dice "$0 retenidos" es
                      ruido que hace dudar.
                    */}
                    {[depositState, depositClaimed]
                      .filter((linea) => linea !== null)
                      .map((linea, indice) => (
                        <View
                          key={linea.labelKey}
                          testID={
                            indice === 0 && depositState !== null
                              ? 'trip-detail.deposit'
                              : 'trip-detail.deposit-claimed'
                          }
                          style={{
                            marginTop: SPACING.sm,
                            paddingTop: SPACING.sm,
                            borderTopWidth: 1,
                            borderTopColor: colors.border,
                            gap: 2,
                          }}
                        >
                          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                            <Text
                              style={{
                                fontSize: 14,
                                fontFamily: FONTS.body,
                                color: colors.textSecondary,
                              }}
                            >
                              {t(`booking.${linea.labelKey}`)}
                            </Text>
                            <Text
                              style={{
                                fontSize: 14,
                                fontFamily: FONTS.body,
                                color: colors.textSecondary,
                              }}
                            >
                              {formatCurrency(linea.amountCents)}
                            </Text>
                          </View>
                          <Text
                            style={{
                              fontSize: 12,
                              fontFamily: FONTS.body,
                              color: colors.textMuted,
                            }}
                          >
                            {t(`booking.${linea.noteKey}`)}
                          </Text>
                        </View>
                      ))}
                  </Animated.View>

                  <InspectionAssuranceBlock
                    checkinInspection={checkinInspection}
                    checkoutInspection={checkoutInspection}
                    colors={colors}
                    enter={enter}
                  />

                  {/* Lo que quedo registrado del traspaso: los danos marcados y
                      las firmas. Las dos cosas se exigian, se guardaban, y
                      ninguna pantalla las mostraba -- evidencia que nadie puede
                      consultar es almacenamiento, no evidencia. */}
                  <Animated.View collapsable={false} entering={enter(6)}>
                    <InspectionEvidenceBlock
                      checkinInspection={checkinInspection}
                      checkoutInspection={checkoutInspection}
                    />
                  </Animated.View>

                  {/* Counterparty card */}
                  <Animated.View
                    testID="trip-detail.host-info"
                    collapsable={false}
                    entering={enter(5)}
                    style={{
                      marginTop: SPACING.md,
                      backgroundColor: colors.surface,
                      borderRadius: RADIUS.lg,
                      padding: SPACING.md,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: SPACING.md,
                      ...WEB_SHADOWS.sm,
                    }}
                  >
                    <Avatar
                      size="md"
                      source={counterpartyAvatarUrl}
                      fallback={counterpartyName ?? '?'}
                      accessibilityLabel={counterpartyName ?? ''}
                    />
                    <View style={{ flex: 1 }}>
                      <Text
                        style={{
                          fontSize: 15,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                      >
                        {counterpartyName}
                      </Text>
                      <Text
                        style={{
                          fontSize: 13,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                        }}
                      >
                        {counterpartyLabel}
                      </Text>
                    </View>
                    <Button
                      onPress={async () => {
                        if (!user || !counterpartyId) return
                        try {
                          const conversationId = await messagingService.getOrCreateConversation(
                            user.id,
                            counterpartyId,
                            booking.id,
                          )
                          router.push(`/(tabs)/messages/${conversationId}`)
                        } catch (err) {
                          /*
                           * UN TOAST QUE DICE "Error" NO ES UNA RESPUESTA.
                           *
                           * El chat solo se abre con una reserva pagada, y esta
                           * pantalla recibe TODOS los estados: hoy hay 17
                           * reservas canceladas en produccion --incluidas las
                           * tres que murieron sin pagar, de $1.610, $253 y
                           * $74,75-- y desde todas ellas este boton llama a la
                           * base, que lo rechaza con 42501. El catch descartaba
                           * el ServiceError y mostraba `common.error`, que en
                           * es.json es literalmente "Error".
                           *
                           * El mismo callejon se arreglo hoy en la ficha del
                           * carro (`explainTheLock`) y esta copia quedo afuera.
                           * Se dice lo mismo aca: cuando el candado es la razon,
                           * se explica; cualquier otra cosa sigue siendo un
                           * error de verdad y se reporta como tal.
                           */
                          if ((err as { code?: string })?.code === '42501') {
                            void notification(NotificationType.Warning)
                            toast.info(t('messages.locked_until_paid'))
                            return
                          }

                          if (__DEV__) console.error('[TripDetail] contact host failed:', err)
                          toast.error(errorMessage(err, t('common.error')))
                          captureError(err, { screen: 'trips/detail', action: 'contact-host' })
                        }
                      }}
                      variant="secondary"
                      size="sm"
                      title={contactCounterpartyLabel}
                      icon={<MessageCircle size={16} color={COLORS.coral.DEFAULT} />}
                      testID="trip-detail.contact-host"
                    />
                  </Animated.View>

                  {/* Action buttons based on status */}
                  <Animated.View
                    collapsable={false}
                    entering={enter(6)}
                    style={{ marginTop: SPACING.lg, gap: SPACING.sm }}
                  >
                    {booking.status === 'pending_payment' && (
                      <Button
                        onPress={() => {
                          // Hydrate the checkout store from the pending rental so the
                          // payment screen can skip booking-creation and run straight
                          // to Wompi with the existing rental id.
                          const startDate = bookingDateKey(booking.startAt)
                          const endDate = bookingDateKey(booking.endAt)
                          const startTime = bookingTimeHHmm(booking.startAt)
                          const endTime = bookingTimeHHmm(booking.endAt)
                          useBookingStore.setState({
                            vehicleId: booking.vehicleId,
                            startDate,
                            endDate,
                            startTime,
                            endTime,
                            insuranceTier: 'platform',
                            pickupAddress: booking.pickupAddress,
                            dropoffAddress: booking.dropoffAddress,
                            isSameLocation: booking.isSameLocation,
                            pricing: booking.pricing,
                          })
                          router.push(`/booking/checkout?rentalId=${booking.id}`)
                        }}
                        variant="primary"
                        size="lg"
                        title={t('payment.complete_payment')}
                        testID="trip-detail.complete-payment-button"
                      />
                    )}
                    {(booking.status === 'confirmed' || booking.status === 'pending_pickup') && (
                      <Button
                        onPress={() => router.push(`/checkin/${booking.id}`)}
                        variant="primary"
                        size="lg"
                        title={t('checkin.confirm_checkin')}
                        testID="trip-detail.checkin-button"
                      />
                    )}
                    {(booking.status === 'active' || booking.status === 'pending_return') && (
                      <Button
                        onPress={() => router.push(`/checkin/checkout/${booking.id}`)}
                        variant="primary"
                        size="lg"
                        title={t('trip_checkout.confirm_checkout')}
                        testID="trip-detail.checkout-button"
                      />
                    )}
                    {/* Ambos participantes califican el viaje con el mismo CTA.
                        Solo se ofrece cuando el servidor confirma que el viaje
                        está pagado, terminó y sigue dentro de la ventana. */}
                    {booking.status === 'completed' &&
                      (isGuestViewer || isHostViewer) &&
                      tripReviewEligibility.data?.canReview === true && (
                        <Button
                          onPress={() => router.push(`/booking/review/${booking.id}`)}
                          variant="primary"
                          size="lg"
                          title={t('reviews.review_trip_cta')}
                          testID="trip-detail.review-button"
                        />
                      )}
                    {/* Report-damage CTA — surfaces only after the trip is completed
                AND within 14 days of the recorded end. Shows either "File new"
                or "View existing" depending on whether a dispute already
                exists for this trip. CNR ToS §8.3 sets the 14-day window. */}
                    {booking.status === 'completed' && (
                      <ReportDamageBlock
                        rentalId={booking.id}
                        endAt={booking.endAt}
                        colors={colors}
                        onFile={() => router.push(`/booking/dispute/file?bookingId=${booking.id}`)}
                        onView={(disputeId) =>
                          router.push(`/booking/dispute/${disputeId}` as never)
                        }
                      />
                    )}
                    {(booking.status === 'pending_payment' ||
                      (booking.status === 'awaiting_host' && isGuestViewer) ||
                      booking.status === 'confirmed' ||
                      booking.status === 'pending_pickup') && (
                      <Button
                        onPress={() => router.push(`/booking/cancel/${booking.id}`)}
                        variant="secondary"
                        size="lg"
                        title={t('trips.cancel_trip')}
                        testID="trip-detail.cancel-button"
                      />
                    )}
                  </Animated.View>
                </View>
              </ScrollView>
            )
          }}
        </ScreenState>
      </View>
    </ScreenContainer>
  )
}

const REPORT_DAMAGE_WINDOW_DAYS = 14

function InspectionAssuranceBlock({
  checkinInspection,
  checkoutInspection,
  colors,
  enter,
}: {
  checkinInspection: InspectionDetail | null | undefined
  checkoutInspection: InspectionDetail | null | undefined
  colors: ReturnType<typeof useSemanticColors>
  /** Owned by the screen, not by this block: the latch must outlive the remount. */
  enter: EntranceFn
}) {
  const { t } = useTranslation()
  const reviews = [
    { label: t('checkin.title'), review: checkinInspection?.assuranceReview ?? null },
    { label: t('trip_checkout.title'), review: checkoutInspection?.assuranceReview ?? null },
  ].filter((item) => item.review)

  if (reviews.length === 0) return null

  return (
    <Animated.View
      testID="trip-detail.inspection-assurance"
      collapsable={false}
      entering={enter(5)}
      style={{
        marginTop: SPACING.md,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        gap: SPACING.sm,
        ...WEB_SHADOWS.sm,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
        <Shield size={18} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />
        <Text style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
          {t('inspection_assurance.title')}
        </Text>
      </View>

      {reviews.map(({ label, review }) => {
        if (!review) return null
        const state = assuranceStatusState(review.status, colors)
        const Icon = state.icon
        return (
          <View
            key={review.id}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              paddingTop: SPACING.xs,
            }}
          >
            <Icon size={16} color={state.color} strokeWidth={2} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 13, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
                {label}
              </Text>
              <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}>
                {t(`inspection_assurance.status_${review.status}`)}
              </Text>
            </View>
            <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
              {formatAssuranceDueAt(review.dueAt, t)}
            </Text>
          </View>
        )
      })}
    </Animated.View>
  )
}

function assuranceStatusState(
  status: InspectionAssuranceStatus,
  colors: ReturnType<typeof useSemanticColors>,
) {
  if (status === 'accepted') return { icon: CheckCircle, color: colors.success }
  if (['rejected', 'failed', 'expired', 'needs_action'].includes(status)) {
    return { icon: AlertTriangle, color: COLORS.coral.DEFAULT }
  }
  return { icon: Clock3, color: colors.warning }
}

/**
 * "SLA" is an internal acronym and "23h" is not a translated string — both
 * used to reach the renter untouched. Everything goes through i18n now.
 */
function formatAssuranceDueAt(
  value: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const dueAt = new Date(value).getTime()
  const diffMs = dueAt - Date.now()
  if (!Number.isFinite(diffMs)) return ''
  if (diffMs <= 0) return t('inspection_assurance.review_overdue')
  const hours = Math.ceil(diffMs / (60 * 60 * 1000))
  return t('inspection_assurance.due_in_hours', { count: hours })
}

function ReportDamageBlock({
  rentalId,
  endAt,
  colors,
  onFile,
  onView,
}: {
  rentalId: string
  endAt: string | undefined
  colors: ReturnType<typeof useSemanticColors>
  onFile: () => void
  onView: (disputeId: string) => void
}) {
  const { t } = useTranslation()
  const { data: disputes } = useTripDisputes(rentalId)

  const windowOpen = (() => {
    if (!endAt) return true // unknown end ⇒ permissive
    const ageMs = Date.now() - new Date(endAt).getTime()
    return ageMs <= REPORT_DAMAGE_WINDOW_DAYS * 24 * 60 * 60 * 1000
  })()

  const existing = (disputes ?? [])[0] ?? null

  // If there's already a dispute, link to it regardless of window state —
  // viewing past disputes should always be possible.
  if (existing) {
    return (
      <Pressable
        onPress={() => onView(existing.id)}
        accessibilityRole="button"
        accessibilityLabel={t('disputes.view_existing_cta')}
        testID="trip-detail.view-dispute"
        style={({ pressed }) => ({
          opacity: pressed ? 0.85 : 1,
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.sm,
          padding: SPACING.md,
          borderRadius: RADIUS.lg,
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.border,
          ...WEB_SHADOWS.sm,
        })}
      >
        <AlertTriangle size={18} color={COLORS.coral.DEFAULT} />
        <View style={{ flex: 1, gap: 4 }}>
          <Text style={{ fontSize: 14, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
            {t('disputes.view_existing_cta')}
          </Text>
          <DisputeStatusPill status={existing.status} />
        </View>
      </Pressable>
    )
  }

  // No existing dispute + window closed = no CTA. Quietly hides the section
  // rather than showing an inactive button that confuses the user.
  if (!windowOpen) return null

  return (
    <Pressable
      onPress={onFile}
      accessibilityRole="button"
      accessibilityLabel={t('disputes.report_damage_cta')}
      testID="trip-detail.report-damage"
      style={({ pressed }) => ({
        opacity: pressed ? 0.85 : 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        ...WEB_SHADOWS.sm,
      })}
    >
      <AlertTriangle size={18} color={colors.textSecondary} strokeWidth={1.8} />
      <View style={{ flex: 1, gap: 2 }}>
        <Text style={{ fontSize: 14, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
          {t('disputes.report_damage_cta')}
        </Text>
        <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
          {t('disputes.report_damage_subtitle')}
        </Text>
      </View>
    </Pressable>
  )
}
